import { ArrowRight, Calendar, Clipboard, Clock } from '../icons';
import InteractiveCard from '../common/interactiveCard';
import { useTranslation } from 'react-i18next';
import CardData from '../dashboard/cardData';
import { useLanguage } from '../../context/LanguageContext';
import { LOCAL_STORAGE_NAMES, ROUTES, ThemeMode } from '../../constants/constants';
import { IWorkTime } from '../../interface/workTimes';
import dayjs from 'dayjs';
import { Divider, IconButton, Tooltip } from '../Ant';
import { useNavigationStack } from '../../context/NavigationStackContext';
import { rgbToRgba, workTimeFallBackColor } from '../../constants/utilityFunctions';
import { themes } from '../config/variables';
import { hasGoodContrast } from '../../helpers/colorContrastRatioHelper';

const WorkTimeCard = ({ workTime, onPress }: { workTime: IWorkTime; onPress?: () => void }) => {
  const { t } = useTranslation();
  const { getAppLanguageCode } = useLanguage();
  const { push } = useNavigationStack();
  const storedTheme = localStorage.getItem(LOCAL_STORAGE_NAMES.theme);
  const translatedStatus =
    workTime?.workTimeType?.translations?.find((t) => t.language === getAppLanguageCode())
      ?.translation ?? t('worktime:typeNotDefined');
  const workTimeColor =
    workTime.workTimeType?.color ?? workTimeFallBackColor(storedTheme as ThemeMode);
  const isLightTheme = storedTheme === ThemeMode.light.toLowerCase();
  const lightBackgroundColor = `rgb(${themes['light']['clr-container-bg']})`;
  let borderColor = workTimeColor;
  if (isLightTheme && !hasGoodContrast(workTimeColor, lightBackgroundColor)) {
    borderColor = '#E3E3E3';
  }
  return (
    <InteractiveCard
      data-testid="work-time-card"
      className={` task-card w-full`}
      onActivate={onPress}
      style={{ border: `1px solid ${borderColor}` }}
    >
      <div className="d-flex justify-between pb-10 items-center">
        <div className="d-flex gap-5 items-center">
          <div
            data-testid="worktime-card-status"
            className="status-chip d-flex justify-center items-center"
            style={{ backgroundColor: `${rgbToRgba(workTimeColor, 0.3)}` }}
          >
            {translatedStatus}
          </div>
        </div>
        <div className="d-flex gap-10">
          <Tooltip title={t('transaction:goDetail')} placement="top" arrow={false}>
            <IconButton
              aria-label={t('transaction:goDetail')}
              data-testid={`worktime-card-detail-button`}
              icon={<ArrowRight />}
              onClick={() => {
                push(ROUTES.WORK_TIME_DETAIL + '/' + workTime.id);
              }}
            />
          </Tooltip>
        </div>
      </div>
      <Divider className="mb-10" />
      <div className="d-flex flex-col gap-10">
        <CardData
          type="subject"
          icon={<Clipboard />}
          title={t('common:subject')}
          content={workTime.subject}
          contentclass="lineclamp-1"
        />
        <div className="d-flex justify-between">
          <CardData
            type="startTime"
            icon={<Calendar />}
            title={t('worktime:startTime')}
            content={dayjs(workTime.startTime).format('MMM DD, YYYY, HH:mm')}
          />
          <CardData
            type="endTime"
            icon={<Calendar />}
            title={t('worktime:endTime')}
            content={dayjs(workTime.endTime).format('MMM DD, YYYY, HH:mm')}
          />
        </div>
        <CardData
          type="duration"
          icon={<Clock />}
          title={t('worktime:duration')}
          content={`${workTime.duration} ${t('common:h')}`}
        />
      </div>
    </InteractiveCard>
  );
};

export default WorkTimeCard;
